import React, { useState, useCallback, useEffect } from 'react';
import '../styles/STPSubmissions.css';
import {
    FiSend, FiList, FiEdit2, FiShield,
    FiRefreshCw, FiChevronDown, FiChevronUp,
    FiCheckCircle, FiAlertTriangle, FiXCircle, FiClock,
} from 'react-icons/fi';
import Cookies from 'js-cookie';
import CommonLoader from '../../../components/common/CommonLoader';
import { GRAPHQL_HTTP_URL } from '../../../config/api';

const GQL = GRAPHQL_HTTP_URL;

// ── GraphQL ────────────────────────────────────────────────────────────────

const VALIDATE_QUERY = `
  query ValidateSTPPayload($payPeriodStart: String!, $payPeriodEnd: String!) {
    validateSTPPayload(payPeriodStart: $payPeriodStart, payPeriodEnd: $payPeriodEnd) {
      isValid
      errors   { code message field severity }
      warnings { code message field severity }
      financials { totalGrossPay totalPaygTax totalSuper employeeCount }
    }
  }
`;

const SUBMIT_PAYEVENT = `
  mutation SubmitSTPPayEvent($input: SubmitSTPInput!) {
    submitSTPPayEvent(input: $input) {
      success
      message
      submission {
        id status atoReceiptId atoStatusMessage
        totalGrossPay totalPaygTax totalSuper employeeCount
      }
    }
  }
`;

const SUBMIT_UPDATE_EVENT = `
  mutation SubmitSTPUpdateEvent($input: SubmitUpdateEventInput!) {
    submitSTPUpdateEvent(input: $input) {
      success
      message
      submission { id status atoReceiptId atoStatusMessage }
    }
  }
`;

const RETRY_MUTATION = `
  mutation RetrySTPSubmission($input: RetryStpInput!) {
    retrySTPSubmission(input: $input) {
      success
      message
      submission { id status retryCount atoReceiptId atoStatusMessage }
    }
  }
`;

const GET_SUBMISSIONS = `
  query {
    stpSubmissions {
      id createdAt
      payPeriodStart payPeriodEnd paymentDate
      submissionType status
      atoReceiptId atoStatusMessage atoResponse
      totalGrossPay totalPaygTax totalSuper employeeCount
      retryCount lastRetryAt submittedAt respondedAt
      submittedBy environment validationErrors
    }
  }
`;

const CREDENTIALS_QUERY = `
  query {
    stpCredentialsConfigured
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────

const gql = async (query, variables = {}) => {
    const token = Cookies.get('accessToken');
    const res = await fetch(GQL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
};

const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = String(d).split('T')[0].split('-');
    return `${day}/${m}/${y}`;
};

const fmtMoney = (val) =>
    `$${Number(val ?? 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const today = () => new Date().toISOString().split('T')[0];

const STATUS_META = {
    SUCCESS:  { label: 'Success',  cls: 'stp-badge--success',  Icon: FiCheckCircle   },
    PENDING:  { label: 'Pending',  cls: 'stp-badge--pending',  Icon: FiClock         },
    FAILED:   { label: 'Failed',   cls: 'stp-badge--failed',   Icon: FiXCircle       },
    REJECTED: { label: 'Rejected', cls: 'stp-badge--rejected', Icon: FiXCircle       },
    WARNING:  { label: 'Warning',  cls: 'stp-badge--warning',  Icon: FiAlertTriangle },
};

const TABS = [
    { key: 'submit',     label: 'Submit PAYEVENT',  Icon: FiSend    },
    { key: 'history',    label: 'History',          Icon: FiList    },
    { key: 'correction', label: 'Correction',       Icon: FiEdit2   },
    { key: 'settings',   label: 'Settings',         Icon: FiShield  },
];

// ── Sub-components ─────────────────────────────────────────────────────────

function ValidationResult({ result }) {
    if (!result) return null;
    const { isValid, errors, warnings, financials } = result;
    return (
        <div className={`stp-validation ${isValid ? 'stp-validation--ok' : 'stp-validation--err'}`}>
            <div className="stp-validation-header">
                {isValid
                    ? <><FiCheckCircle className="stp-v-icon stp-v-icon--ok" /> Payload is valid</>
                    : <><FiXCircle     className="stp-v-icon stp-v-icon--err" /> Validation failed</>
                }
                {warnings?.length > 0 && (
                    <span className="stp-v-warn-count">
                        <FiAlertTriangle size={13} /> {warnings.length} warning{warnings.length > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {financials && (
                <div className="stp-fin-row">
                    <span><strong>{financials.employeeCount}</strong> employees</span>
                    <span>Gross <strong>{fmtMoney(financials.totalGrossPay)}</strong></span>
                    <span>Tax <strong>{fmtMoney(financials.totalPaygTax)}</strong></span>
                    <span>Super <strong>{fmtMoney(financials.totalSuper)}</strong></span>
                </div>
            )}

            {errors?.map((e, i) => (
                <div key={i} className="stp-v-item stp-v-item--error">
                    <FiXCircle size={12} />
                    <span><strong>{e.code}</strong>{e.field ? ` [${e.field}]` : ''}: {e.message}</span>
                </div>
            ))}
            {warnings?.map((w, i) => (
                <div key={i} className="stp-v-item stp-v-item--warning">
                    <FiAlertTriangle size={12} />
                    <span><strong>{w.code}</strong>{w.field ? ` [${w.field}]` : ''}: {w.message}</span>
                </div>
            ))}
        </div>
    );
}

function SubmissionRow({ sub, onRetry, retrying }) {
    const [open, setOpen] = useState(false);
    const meta = STATUS_META[sub.status] ?? STATUS_META.PENDING;
    const canRetry = (sub.status === 'FAILED' || sub.status === 'REJECTED') && sub.retryCount < 3;

    let validationErrors = null;
    try { validationErrors = sub.validationErrors ? JSON.parse(sub.validationErrors) : null; } catch (_) {}

    return (
        <>
            <tr className="stp-row" onClick={() => setOpen(o => !o)}>
                <td className="stp-period">
                    {fmtDate(sub.payPeriodStart)} – {fmtDate(sub.payPeriodEnd)}
                </td>
                <td>
                    <span className="stp-type-badge">
                        {sub.submissionType === 'UPDATEEVENT' ? 'UPDATE' : 'PAY'}
                    </span>
                </td>
                <td>
                    <span className={`stp-badge ${meta.cls}`}>
                        <meta.Icon size={11} /> {meta.label}
                    </span>
                </td>
                <td className="stp-center">{sub.employeeCount ?? '—'}</td>
                <td className="stp-amount">{fmtMoney(sub.totalGrossPay)}</td>
                <td className="stp-amount">{fmtMoney(sub.totalPaygTax)}</td>
                <td className="stp-amount">{fmtMoney(sub.totalSuper)}</td>
                <td className="stp-dt">{fmtDateTime(sub.createdAt)}</td>
                <td>
                    <div className="stp-row-actions" onClick={e => e.stopPropagation()}>
                        {canRetry && (
                            <button
                                className="stp-btn stp-btn--retry"
                                onClick={() => onRetry(sub.id)}
                                disabled={retrying === sub.id}
                                title={`Retry (${sub.retryCount}/3 attempts used)`}
                            >
                                <FiRefreshCw className={retrying === sub.id ? 'stp-spin' : ''} />
                                {retrying === sub.id ? '…' : 'Retry'}
                            </button>
                        )}
                        <button
                            className="stp-btn stp-btn--expand"
                            onClick={() => setOpen(o => !o)}
                            title="View details"
                        >
                            {open ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                    </div>
                </td>
            </tr>

            {open && (
                <tr className="stp-detail-row">
                    <td colSpan={9}>
                        <div className="stp-detail">
                            <div className="stp-detail-grid">
                                <div><span className="stp-dl">Receipt ID</span><span className="stp-dv">{sub.atoReceiptId || '—'}</span></div>
                                <div><span className="stp-dl">ATO Message</span><span className="stp-dv">{sub.atoStatusMessage || '—'}</span></div>
                                <div><span className="stp-dl">Submitted At</span><span className="stp-dv">{fmtDateTime(sub.submittedAt)}</span></div>
                                <div><span className="stp-dl">Responded At</span><span className="stp-dv">{fmtDateTime(sub.respondedAt)}</span></div>
                                <div><span className="stp-dl">Payment Date</span><span className="stp-dv">{fmtDate(sub.paymentDate)}</span></div>
                                <div><span className="stp-dl">Environment</span><span className="stp-dv">{sub.environment || '—'}</span></div>
                                <div><span className="stp-dl">Retry Count</span><span className="stp-dv">{sub.retryCount}</span></div>
                                <div><span className="stp-dl">Submitted By</span><span className="stp-dv">{sub.submittedBy || '—'}</span></div>
                            </div>

                            {validationErrors?.length > 0 && (
                                <div className="stp-detail-errors">
                                    <p className="stp-detail-errors-title">Validation Errors from ATO</p>
                                    {validationErrors.map((e, i) => (
                                        <div key={i} className="stp-v-item stp-v-item--error">
                                            <FiXCircle size={12} />
                                            <span><strong>{e.code || e.Code}</strong>: {e.message || e.Text || e.Description}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// ── Submit Tab ─────────────────────────────────────────────────────────────

function SubmitTab({ onSubmitted }) {
    const [form, setForm]           = useState({ payPeriodStart: '', payPeriodEnd: '', paymentDate: today() });
    const [validation, setValidation] = useState(null);
    const [validating, setValidating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult]       = useState(null);   // { success, message }
    const [error, setError]         = useState('');

    const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setValidation(null); setResult(null); setError(''); };

    const handleValidate = async () => {
        if (!form.payPeriodStart || !form.payPeriodEnd) { setError('Please set both period dates.'); return; }
        setValidating(true); setError(''); setValidation(null); setResult(null);
        try {
            const data = await gql(VALIDATE_QUERY, { payPeriodStart: form.payPeriodStart, payPeriodEnd: form.payPeriodEnd });
            setValidation(data.validateSTPPayload);
        } catch (e) { setError(e.message); }
        finally { setValidating(false); }
    };

    const handleSubmit = async () => {
        if (!form.payPeriodStart || !form.payPeriodEnd || !form.paymentDate) {
            setError('All date fields are required.'); return;
        }
        setSubmitting(true); setError(''); setResult(null);
        try {
            const data = await gql(SUBMIT_PAYEVENT, { input: form });
            const r = data.submitSTPPayEvent;
            setResult({ success: r.success, message: r.message });
            if (r.success) onSubmitted();
        } catch (e) { setError(e.message); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="stp-section">
            <h3 className="stp-section-title">Submit PAYEVENT to ATO</h3>
            <p className="stp-section-sub">
                Submits YTD payroll data for all finalized payslips in the selected period.
            </p>

            {error  && <div className="stp-error">{error}</div>}
            {result && (
                <div className={result.success ? 'stp-success' : 'stp-error'}>
                    {result.success ? <FiCheckCircle /> : <FiXCircle />} {result.message}
                </div>
            )}

            <div className="stp-form">
                <label className="stp-label">
                    Pay Period Start
                    <input
                        type="date"
                        className="stp-input"
                        value={form.payPeriodStart}
                        onChange={e => set('payPeriodStart', e.target.value)}
                    />
                </label>
                <label className="stp-label">
                    Pay Period End
                    <input
                        type="date"
                        className="stp-input"
                        value={form.payPeriodEnd}
                        onChange={e => set('payPeriodEnd', e.target.value)}
                    />
                </label>
                <label className="stp-label">
                    Payment Date
                    <input
                        type="date"
                        className="stp-input"
                        value={form.paymentDate}
                        onChange={e => set('paymentDate', e.target.value)}
                    />
                </label>
            </div>

            <div className="stp-form-actions">
                <button className="stp-btn stp-btn--validate" onClick={handleValidate} disabled={validating || submitting}>
                    <FiCheckCircle className={validating ? 'stp-spin' : ''} />
                    {validating ? 'Validating…' : 'Validate Payload'}
                </button>
                <button
                    className="stp-btn stp-btn--submit"
                    onClick={handleSubmit}
                    disabled={submitting || validating || (validation && !validation.isValid)}
                    title={validation && !validation.isValid ? 'Fix validation errors before submitting' : ''}
                >
                    <FiSend className={submitting ? 'stp-spin' : ''} />
                    {submitting ? 'Submitting…' : 'Submit PAYEVENT'}
                </button>
            </div>

            <ValidationResult result={validation} />
        </div>
    );
}

// ── History Tab ────────────────────────────────────────────────────────────

function HistoryTab({ submissions, loading, onRefresh, onRetry, retrying }) {
    return (
        <div className="stp-section">
            <div className="stp-history-header">
                <div>
                    <h3 className="stp-section-title">Submission History</h3>
                    <p className="stp-section-sub">All STP submissions sent to the ATO for this company.</p>
                </div>
                <button className="stp-btn stp-btn--refresh" onClick={onRefresh} disabled={loading}>
                    <FiRefreshCw className={loading ? 'stp-spin' : ''} />
                    Refresh
                </button>
            </div>

            {loading && !submissions && (
                <div className="stp-loader"><CommonLoader /></div>
            )}

            {!loading && submissions?.length === 0 && (
                <div className="stp-empty">
                    <FiList size={40} className="stp-empty-icon" />
                    <p>No submissions yet.</p>
                    <p className="stp-empty-hint">Submit a PAYEVENT to see history here.</p>
                </div>
            )}

            {submissions?.length > 0 && (
                <div className="stp-card">
                    <table className="stp-table">
                        <thead>
                            <tr>
                                <th>Period</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Employees</th>
                                <th>Gross</th>
                                <th>Tax</th>
                                <th>Super</th>
                                <th>Submitted</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {submissions.map(sub => (
                                <SubmissionRow
                                    key={sub.id}
                                    sub={sub}
                                    onRetry={onRetry}
                                    retrying={retrying}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Correction Tab ─────────────────────────────────────────────────────────

function CorrectionTab({ submissions, onSubmitted }) {
    const successfulPayEvents = (submissions ?? []).filter(
        s => s.submissionType === 'PAYEVENT' && s.status === 'SUCCESS'
    );

    const [selectedId, setSelectedId]   = useState('');
    const [form, setForm]               = useState({ payPeriodStart: '', payPeriodEnd: '', paymentDate: today() });
    const [submitting, setSubmitting]   = useState(false);
    const [result, setResult]           = useState(null);
    const [error, setError]             = useState('');

    const handleSelect = (id) => {
        setSelectedId(id);
        setResult(null); setError('');
        if (!id) { setForm(f => ({ ...f, payPeriodStart: '', payPeriodEnd: '' })); return; }
        const sub = successfulPayEvents.find(s => s.id === id);
        if (sub) setForm(f => ({ ...f, payPeriodStart: sub.payPeriodStart, payPeriodEnd: sub.payPeriodEnd }));
    };

    const handleSubmit = async () => {
        if (!form.payPeriodStart || !form.payPeriodEnd || !form.paymentDate) {
            setError('All date fields are required.'); return;
        }
        setSubmitting(true); setError(''); setResult(null);
        try {
            const input = {
                payPeriodStart: form.payPeriodStart,
                payPeriodEnd:   form.payPeriodEnd,
                paymentDate:    form.paymentDate,
                ...(selectedId ? { originalSubmissionId: selectedId } : {}),
            };
            const data = await gql(SUBMIT_UPDATE_EVENT, { input });
            const r = data.submitSTPUpdateEvent;
            setResult({ success: r.success, message: r.message });
            if (r.success) onSubmitted();
        } catch (e) { setError(e.message); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="stp-section">
            <h3 className="stp-section-title">Submit UPDATEEVENT (Correction)</h3>
            <p className="stp-section-sub">
                Use this to correct a previously submitted PAYEVENT. The ATO replaces the original with corrected YTD figures.
            </p>

            <div className="stp-info-banner">
                An UPDATEEVENT replaces YTD data for all employees in the period. Ensure payslips have been corrected before submitting.
            </div>

            {error  && <div className="stp-error">{error}</div>}
            {result && (
                <div className={result.success ? 'stp-success' : 'stp-error'}>
                    {result.success ? <FiCheckCircle /> : <FiXCircle />} {result.message}
                </div>
            )}

            <div className="stp-form">
                <label className="stp-label">
                    Original PAYEVENT (optional)
                    <select
                        className="stp-input"
                        value={selectedId}
                        onChange={e => handleSelect(e.target.value)}
                    >
                        <option value="">— Select original submission —</option>
                        {successfulPayEvents.map(s => (
                            <option key={s.id} value={s.id}>
                                {fmtDate(s.payPeriodStart)} – {fmtDate(s.payPeriodEnd)}
                                {s.atoReceiptId ? ` (${s.atoReceiptId})` : ''}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="stp-label">
                    Pay Period Start
                    <input
                        type="date"
                        className="stp-input"
                        value={form.payPeriodStart}
                        onChange={e => setForm(f => ({ ...f, payPeriodStart: e.target.value }))}
                    />
                </label>
                <label className="stp-label">
                    Pay Period End
                    <input
                        type="date"
                        className="stp-input"
                        value={form.payPeriodEnd}
                        onChange={e => setForm(f => ({ ...f, payPeriodEnd: e.target.value }))}
                    />
                </label>
                <label className="stp-label">
                    Payment Date
                    <input
                        type="date"
                        className="stp-input"
                        value={form.paymentDate}
                        onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))}
                    />
                </label>
            </div>

            <div className="stp-form-actions">
                <button className="stp-btn stp-btn--submit" onClick={handleSubmit} disabled={submitting}>
                    <FiSend className={submitting ? 'stp-spin' : ''} />
                    {submitting ? 'Submitting…' : 'Submit UPDATEEVENT'}
                </button>
            </div>
        </div>
    );
}

// ── Settings Tab ───────────────────────────────────────────────────────────

function SettingsTab() {
    const [configured, setConfigured] = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState('');

    useEffect(() => {
        (async () => {
            try {
                const data = await gql(CREDENTIALS_QUERY);
                setConfigured(data.stpCredentialsConfigured);
            } catch (e) { setError(e.message); }
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="stp-section">
            <h3 className="stp-section-title">STP Configuration</h3>
            <p className="stp-section-sub">ATO credential status and environment information.</p>

            {error && <div className="stp-error">{error}</div>}

            {loading && <div className="stp-loader"><CommonLoader /></div>}

            {!loading && !error && (
                <div className="stp-settings-card">
                    <div className="stp-settings-row">
                        <span className="stp-settings-label">ATO DSP Credentials</span>
                        <span className={`stp-settings-value ${configured ? 'stp-settings-value--ok' : 'stp-settings-value--err'}`}>
                            {configured
                                ? <><FiCheckCircle /> Configured</>
                                : <><FiXCircle /> Not configured</>
                            }
                        </span>
                    </div>
                    <div className="stp-settings-row">
                        <span className="stp-settings-label">Environment</span>
                        <span className="stp-settings-value">
                            {process.env.NODE_ENV === 'production' ? 'Production (live ATO)' : 'Development (ATO test gateway)'}
                        </span>
                    </div>
                    <div className="stp-settings-row">
                        <span className="stp-settings-label">ATO Endpoint</span>
                        <span className="stp-settings-value stp-settings-value--mono">
                            {process.env.NODE_ENV === 'production'
                                ? 'https://api.ato.gov.au'
                                : 'https://test-api.ato.gov.au'
                            }
                        </span>
                    </div>

                    {!configured && (
                        <div className="stp-info-banner" style={{ marginTop: 16 }}>
                            Add <code>STP_SOFTWARE_ID</code> and <code>STP_MACHINE_CREDENTIAL</code> to your server <code>.env</code> file.
                            These are issued by the ATO when you register as a Digital Service Provider (DSP).
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function STPSubmissions() {
    const [activeTab, setActiveTab]   = useState('submit');
    const [submissions, setSubmissions] = useState(null);
    const [loadingSubs, setLoadingSubs] = useState(false);
    const [retrying, setRetrying]     = useState(null);
    const [error, setError]           = useState('');

    const loadSubmissions = useCallback(async () => {
        setLoadingSubs(true); setError('');
        try {
            const data = await gql(GET_SUBMISSIONS);
            setSubmissions(data.stpSubmissions ?? []);
        } catch (e) { setError(e.message); }
        finally { setLoadingSubs(false); }
    }, []);

    // Load history on mount and whenever history tab is activated
    useEffect(() => {
        if (activeTab === 'history' || activeTab === 'correction') loadSubmissions();
    }, [activeTab, loadSubmissions]);

    const handleRetry = async (id) => {
        setRetrying(id); setError('');
        try {
            await gql(RETRY_MUTATION, { input: { submissionId: id } });
            await loadSubmissions();
        } catch (e) { setError(e.message); }
        finally { setRetrying(null); }
    };

    const handleSubmitted = () => {
        // Refresh history after a successful submission
        loadSubmissions();
        // Switch to history after short delay so user sees the result message first
        setTimeout(() => setActiveTab('history'), 1500);
    };

    return (
        <div className="stp-page">

            {/* Header */}
            <div className="stp-header">
                <div>
                    <h2 className="stp-title">Single Touch Payroll (STP)</h2>
                    <p className="stp-subtitle">
                        ATO-compliant payroll reporting — PAYEVNT.0004 &amp; UPDTEVNT.0001
                    </p>
                </div>
            </div>

            {error && <div className="stp-error">{error}</div>}

            {/* Tab bar */}
            <div className="stp-tabs">
                {TABS.map(({ key, label, Icon }) => (
                    <button
                        key={key}
                        className={`stp-tab${activeTab === key ? ' stp-tab--active' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        <Icon size={14} />
                        {label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            {activeTab === 'submit' && (
                <SubmitTab onSubmitted={handleSubmitted} />
            )}
            {activeTab === 'history' && (
                <HistoryTab
                    submissions={submissions}
                    loading={loadingSubs}
                    onRefresh={loadSubmissions}
                    onRetry={handleRetry}
                    retrying={retrying}
                />
            )}
            {activeTab === 'correction' && (
                <CorrectionTab submissions={submissions} onSubmitted={handleSubmitted} />
            )}
            {activeTab === 'settings' && (
                <SettingsTab />
            )}
        </div>
    );
}
